import { useState, useEffect } from "react";
import { useAppSelector } from "../../../../../store/types";
import SbuxSelect from "../../../../../components/SbuxSelect";
import SbuxButtonGroup, {
  type ButtonItem,
} from "../../../../../components/SbuxButtonGroup";
import { Box, FormControl, InputLabel } from "@mui/material";
import SbuxTextField from "../../../../../components/SubxTextField";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import React from "react";
import SbuxMediaRecorder from "../../../../../components/SbuxMediaRecorder";
import MediaMgtFileUpload from "./MediaMgtFileUpload";
import useTextTranslate from "../../../hooks/useTextTranslate";
import { MEDIAMGT_FILE_NAME_ERROR } from "../../../../../constants";
import MediaTextToSpeech from "./MediaTextToSpeech";
import { getTextLengthErrorMessage } from "../../utils";
import styles from "../../styles";
import useCss from "../../../../../hooks/useCss";
import { getMediaMgtRowsSelector } from "../../../../../selectors/mediaMgtSelector";
import type { MediaInfo } from "../../common/types";

interface LanguageItem {
  value: string;
  name: string;
}

interface MediaRow {
  languageCode?: string;
  language?: string;
  promptName?: string;
  description?: string;
  fileName?: string;
  [key: string]: unknown;
}

interface MediaMgtDataRow {
  promptName?: string;
  [key: string]: unknown;
}

interface Props {
  languages: LanguageItem[];
  row: MediaRow;
  handleMediaMgtRow: (row: MediaRow, hasError?: boolean) => void;
  handleMediaInfo: (info: MediaInfo | null) => void;
  buttonList: ButtonItem[];
  handleDisableSaveBtn: (disabled: boolean) => void;
}

const MediaMgtAdd = ({
  languages,
  row,
  handleMediaMgtRow,
  handleMediaInfo,
  buttonList,
  handleDisableSaveBtn,
}: Props) => {
  const classes = useCss(styles);

  const mediaMgtRows = useAppSelector(getMediaMgtRowsSelector);

  const [toggleButton, setToggleButton] = useState<number>(1);
  const [languageCode, setLanguageCode] = useState(languages[0].value);
  const [promptName, setPromptName] = useState("");
  const [description, setDescription] = useState("");
  const [fileNameError, setFileNameError] = useState(false);
  const [fileNameLengthError, setFileNameLengthError] = useState(false);
  const [descriptionLengthError, setDescriptionLengthError] = useState(false);
  const [searchRowData, setSearchRowData] = useState<MediaMgtDataRow[]>([]);

  useEffect(() => {
    setSearchRowData((mediaMgtRows ?? []) as MediaMgtDataRow[]);
  }, [mediaMgtRows]);

  const handleToggle = (newValue: string) => {
    setToggleButton(Number(newValue));
  };

  const fileNamePlaceHolder = useTextTranslate("File Name");
  const descriptionPlaceHolder = useTextTranslate("Description");

  const handleLanguageChange = (value: string) => {
    const result = languages.find((f: LanguageItem) => f.value === value);
    setLanguageCode(value);
    row.languageCode = value;
    row.language = result?.name;
    handleMediaMgtRow(row);
  };

  const handlePromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (
      searchRowData.some(
        (r) => r.promptName?.toLowerCase() === value.toLowerCase(),
      )
    ) {
      setFileNameError(true);
      row.promptName = "";
    } else {
      setFileNameError(false);
      row.promptName = value;
    }
    let isError = false;
    if (value.length > 50) {
      isError = true;
    }
    setFileNameLengthError(isError);
    setPromptName(value);

    handleMediaMgtRow(row, isError || descriptionLengthError);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    let isError = false;
    if (value.length > 150) {
      isError = true;
    }
    setDescriptionLengthError(isError);

    setDescription(value);
    row.description = value;
    handleMediaMgtRow(row, isError || fileNameLengthError);
  };

  const getErrorMessage = () => {
    if (fileNameError) {
      return MEDIAMGT_FILE_NAME_ERROR;
    }
    if (fileNameLengthError) {
      return getTextLengthErrorMessage(50);
    }
  };

  return (
    <Box>
      <Box
        component="form"
        className={classes.formControl}
        noValidate
        autoComplete="off"
      >
        <Box>
          <SbuxSelect
            label={
              <SbuxTranslate>{`Step 1: Select the language / Target Language`}</SbuxTranslate>
            }
            menuItems={
              languages as import("../../../../../components/SbuxSelect").MenuItem[]
            }
            defaultValue={languageCode}
            value={languageCode}
            handleSelectChange={(value) => handleLanguageChange(value)}
          />
        </Box>
        <Box className="fullWidth">
          <FormControl>
            <InputLabel>
              <SbuxTranslate>{`Step 2: Upload or record a file`}</SbuxTranslate>
            </InputLabel>

            <Box className={classes.mediaDialogTabs}>
              <SbuxButtonGroup
                buttonList={buttonList}
                value={String(toggleButton)}
                handleButtonToggle={handleToggle}
              />
              <Box
                className={`tabContainer ${
                  toggleButton === 3 ? "textToSpeech" : "uploadRecord"
                }`}
              >
                {toggleButton === 1 && (
                  <MediaMgtFileUpload
                    row={row}
                    handleMediaInfo={handleMediaInfo}
                  />
                )}
                {toggleButton === 2 && (
                  <SbuxMediaRecorder
                    handleMediaInfo={
                      handleMediaInfo as (
                        info: Parameters<
                          React.ComponentProps<
                            typeof SbuxMediaRecorder
                          >["handleMediaInfo"]
                        >[0],
                      ) => void
                    }
                  />
                )}
                {toggleButton === 3 && (
                  <MediaTextToSpeech
                    selectedLanguageCode={languageCode}
                    handleMediaInfo={handleMediaInfo}
                    handleDisableSaveBtn={handleDisableSaveBtn}
                  />
                )}
              </Box>
            </Box>
          </FormControl>
        </Box>
        <Box>
          <SbuxTextField
            variant={"outlined"}
            placeholder={fileNamePlaceHolder}
            label={
              <SbuxTranslate>{`Step 3: Please enter a file name`}</SbuxTranslate>
            }
            value={promptName}
            handleChange={handlePromptChange}
            errorMessage={getErrorMessage()}
            maxLength={50}
            isError={fileNameError || fileNameLengthError}
            error={fileNameError || fileNameLengthError}
            displayCounter={true}
          />
        </Box>
        <Box>
          <SbuxTextField
            variant={"outlined"}
            placeholder={descriptionPlaceHolder}
            label={
              <SbuxTranslate>{`Step 4: Description (Optional)`}</SbuxTranslate>
            }
            value={description}
            handleChange={handleDescriptionChange}
            errorMessage={getTextLengthErrorMessage(150)}
            isError={descriptionLengthError}
            error={descriptionLengthError}
            maxLength={150}
            displayCounter={true}
            preventAutoFocus
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MediaMgtAdd;
